// Trade (wholesale) sales: shared by the Sales page's Trade, Invoices and
// Trade prices tabs.
//
// The invoice maths mirrors garageFarmServer helpers/trade.js. The server
// works every total out again when an invoice is saved and its numbers are
// the ones kept; this copy is only for the live preview while editing.
// Change both together.

// ── Pipeline ──────────────────────────────────────────────────────────────────

export const STAGES = [
  { key: 'prospect',   label: 'Prospect',   tone: undefined, hint: 'On the radar, or just said hello' },
  { key: 'interested', label: 'Interested', tone: 'blue',    hint: 'Talking, keen to hear more' },
  { key: 'sampling',   label: 'Sampling',   tone: 'warn',    hint: 'Samples dropped, waiting to hear' },
  { key: 'customer',   label: 'Customer',   tone: 'ok',      hint: 'Ordering' },
  { key: 'lost',       label: 'Lost',       tone: 'bad',     hint: 'Not for now' },
];
export const STAGE = Object.fromEntries(STAGES.map(s => [s.key, s]));
export const OPEN_STAGES = ['prospect', 'interested', 'sampling'];

// Things logged by hand on a business ('stage_change' and 'invoice' entries
// are written by the server).
export const ACTIVITY_TYPES = [
  { key: 'visit',   label: 'Visit' },
  { key: 'call',    label: 'Call' },
  { key: 'message', label: 'Message' },
  { key: 'email',   label: 'Email' },
  { key: 'sample',  label: 'Samples' },
  { key: 'note',    label: 'Note' },
];
export const ACTIVITY_LABEL = {
  ...Object.fromEntries(ACTIVITY_TYPES.map(t => [t.key, t.label])),
  stage_change: 'Stage',
  invoice: 'Invoice',
};

export const PAID_METHODS = [
  { key: 'bank_transfer', label: 'Bank transfer' },
  { key: 'cash',          label: 'Cash' },
  { key: 'card',          label: 'Card' },
  { key: 'other',         label: 'Other' },
];

// ── Money ─────────────────────────────────────────────────────────────────────
// Worked in whole pence so 0.3 kg at £24 comes out at exactly £7.20.

export const lineAmount = (quantity, unitPrice) =>
  Math.round(Number(quantity) * Math.round(Number(unitPrice) * 100)) / 100;

export const invoiceTotal = (items) =>
  (items || []).reduce((pence, it) => pence + Math.round(lineAmount(it.quantity, it.unitPrice) * 100), 0) / 100;

export const formatGBP = (n) =>
  `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Whole pounds, for rough figures like "£40/wk"
export const formatPounds = (n) => `£${Math.round(Number(n || 0)).toLocaleString('en-GB')}`;

export const formatQty = (q) => Number(q).toLocaleString('en-GB', { maximumFractionDigits: 3 });

// Weights read "0.5 kg"; anything counted reads "4 × 50g pack".
const MEASURES = /^(kg|g|l|ml)$/i;
export function quantityLabel(quantity, unit) {
  const q = formatQty(quantity);
  if (!unit) return q;
  return MEASURES.test(unit) ? `${q} ${unit}` : `${q} × ${unit}`;
}

// How a section's prices print on the price list: whole pounds ("£25") when
// every price in it is whole, otherwise all with pence ("£5.50", "£3.00").
export function sectionPriceFormat(items) {
  const anyPence = (items || []).some(it => Math.round(Number(it.price) * 100) % 100 !== 0);
  return (price) => (anyPence ? formatGBP(price) : formatPounds(price));
}

// ── Dates ─────────────────────────────────────────────────────────────────────
// Day-only dates are 'YYYY-MM-DD' here and UTC midnight on the server, so a
// date never shifts with BST. "Today" is the local calendar day.

export function todayYmd(now = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

// A stored date ('2026-10-20T00:00:00.000Z') or a 'YYYY-MM-DD' string -> 'YYYY-MM-DD'
export const ymd = (d) => (d ? String(d instanceof Date ? d.toISOString() : d).slice(0, 10) : '');

export function addDays(ymdStr, days) {
  const d = new Date(`${ymdStr}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export const daysBetween = (from, to) =>
  Math.round((Date.parse(`${ymd(to)}T00:00:00Z`) - Date.parse(`${ymd(from)}T00:00:00Z`)) / 864e5);

export function formatDay(d, { long = false, weekday = false } = {}) {
  if (!d) return '';
  return new Date(`${ymd(d)}T00:00:00Z`).toLocaleDateString('en-GB', {
    ...(weekday ? { weekday: 'short' } : {}),
    day: 'numeric', month: long ? 'long' : 'short', year: 'numeric', timeZone: 'UTC',
  });
}

// "today", "tomorrow", "in 3 days", "2 days ago"
export function relativeDay(d, today = todayYmd()) {
  const n = daysBetween(today, d);
  if (n === 0) return 'today';
  if (n === 1) return 'tomorrow';
  if (n === -1) return 'yesterday';
  return n > 0 ? `in ${n} days` : `${-n} days ago`;
}

// ── Follow-ups and invoice state ──────────────────────────────────────────────

// null (nothing planned), 'undated', 'overdue', 'today' or 'upcoming'
export function followUpState(account, today = todayYmd()) {
  if (!account.nextStep && !account.nextStepDate) return null;
  if (!account.nextStepDate) return 'undated';
  const d = ymd(account.nextStepDate);
  if (d < today) return 'overdue';
  return d === today ? 'today' : 'upcoming';
}

export const isFollowUpDue = (account, today) =>
  account.stage !== 'lost' && ['overdue', 'today'].includes(followUpState(account, today));

// The status as it should read: a sent invoice past its due date is overdue.
export function invoiceState(invoice, today = todayYmd()) {
  if (invoice.status === 'sent' && ymd(invoice.dueDate) < today) return 'overdue';
  return invoice.status;
}

export const INVOICE_STATE = {
  draft:   { label: 'Draft',   tone: undefined },
  sent:    { label: 'Sent',    tone: 'blue' },
  overdue: { label: 'Overdue', tone: 'bad' },
  paid:    { label: 'Paid',    tone: 'ok' },
  void:    { label: 'Void',    tone: 'warn' },
};

export function termsLabel(issueDate, dueDate) {
  const days = daysBetween(issueDate, dueDate);
  if (days <= 0) return 'Due on receipt';
  return `${days} day${days === 1 ? '' : 's'}`;
}

// ── Totals ────────────────────────────────────────────────────────────────────

export function invoiceStats(invoices, today = todayYmd()) {
  const out = { unpaidTotal: 0, unpaidCount: 0, overdueTotal: 0, overdueCount: 0, paidThisMonth: 0, draftCount: 0 };
  const month = today.slice(0, 7);
  for (const inv of invoices || []) {
    const state = invoiceState(inv, today);
    if (state === 'sent' || state === 'overdue') {
      out.unpaidTotal += inv.total;
      out.unpaidCount += 1;
    }
    if (state === 'overdue') {
      out.overdueTotal += inv.total;
      out.overdueCount += 1;
    }
    if (state === 'paid' && ymd(inv.paidAt).slice(0, 7) === month) out.paidThisMonth += inv.total;
    if (state === 'draft') out.draftCount += 1;
  }
  return out;
}

// Per business: what's been invoiced (sent or paid, by invoice date) and
// what's still owed.
export function accountTotals(invoices, today = todayYmd()) {
  const since = addDays(today, -30);
  const map = {};
  for (const inv of invoices || []) {
    if (inv.status === 'draft' || inv.status === 'void') continue;
    if (!map[inv.account]) map[inv.account] = { invoiced: 0, last30: 0, unpaid: 0, overdue: 0, count: 0 };
    const t = map[inv.account];
    t.invoiced += inv.total;
    t.count += 1;
    if (ymd(inv.issueDate) >= since) t.last30 += inv.total;
    if (inv.status === 'sent') {
      t.unpaid += inv.total;
      if (ymd(inv.dueDate) < today) t.overdue += inv.total;
    }
  }
  return map;
}

export function pipelineStats(accounts, invoices, today = todayYmd()) {
  const open = (accounts || []).filter(a => OPEN_STAGES.includes(a.stage));
  const since = addDays(today, -30);
  return {
    openCount: open.length,
    potentialWeekly: open.reduce((s, a) => s + (a.potentialWeekly || 0), 0),
    customers: (accounts || []).filter(a => a.stage === 'customer').length,
    dueFollowUps: (accounts || []).filter(a => isFollowUpDue(a, today)).length,
    invoicedLast30: (invoices || [])
      .filter(i => (i.status === 'sent' || i.status === 'paid') && ymd(i.issueDate) >= since)
      .reduce((s, i) => s + i.total, 0),
  };
}

// ── Email template ────────────────────────────────────────────────────────────
// Same placeholders as the server's fallback: {{name}} {{business}}
// {{number}} {{total}} {{due}}

export const fillTemplate = (template, vars) =>
  String(template || '').replace(/\{\{(\w+)\}\}/g, (m, key) => (key in vars ? vars[key] : m));

export function invoiceTemplateVars(invoice) {
  const contact = String(invoice.billTo?.contactName || '').trim();
  return {
    name:     contact ? contact.split(/\s+/)[0] : (invoice.billTo?.businessName || 'there'),
    business: invoice.billTo?.businessName || '',
    number:   invoice.number || '',
    total:    formatGBP(invoice.total),
    due:      formatDay(invoice.dueDate, { long: true }),
  };
}

// "TDH-0007 The Anchor.pdf", kept to characters the server keeps too
export const invoiceFileName = (invoice) =>
  `${`${invoice.number} ${invoice.billTo?.businessName || ''}`
    .replace(/[^\w .()-]/g, '').replace(/\s+/g, ' ').replace(/[.\s]+$/, '').trim()}.pdf`;

// ── Invoice editor ────────────────────────────────────────────────────────────
// The editor works on a form: dates as 'YYYY-MM-DD', numbers as typed.

export const billToFrom = (account) => ({
  businessName: account?.businessName || '',
  contactName:  account?.contactName || '',
  email:        account?.email || '',
  line1:        account?.address?.line1 || '',
  line2:        account?.address?.line2 || '',
  city:         account?.address?.city || '',
  postcode:     account?.address?.postcode || '',
});

export function invoiceToForm(invoice) {
  return {
    account:   invoice.account || '',
    issueDate: ymd(invoice.issueDate),
    dueDate:   ymd(invoice.dueDate),
    notes:     invoice.notes || '',
    items: (invoice.items || []).map(it => ({
      key:         newKey(),
      date:        ymd(it.date),
      description: it.description,
      quantity:    String(it.quantity),
      unit:        it.unit || '',
      unitPrice:   Number(it.unitPrice).toFixed(2),
    })),
  };
}

export function newInvoiceForm({ account, settings, today }) {
  const terms = account?.termsDays ?? settings?.termsDays ?? 14;
  return { account: account?._id || '', issueDate: today, dueDate: addDays(today, terms), notes: '', items: [] };
}

// A repeat of an earlier invoice: same lines and prices, dated today.
export function duplicateForm(invoice, { account, settings, today }) {
  const form = invoiceToForm(invoice);
  return {
    ...newInvoiceForm({ account, settings, today }),
    account: form.account,
    notes: form.notes,
    items: form.items.map(it => ({ ...it, date: it.date ? today : '' })),
  };
}

export const formToPayload = (form) => ({
  account:   form.account,
  issueDate: form.issueDate,
  dueDate:   form.dueDate,
  notes:     form.notes,
  items: form.items.map(({ key, ...it }) => ({ ...it, date: it.date || null })),
});

// What the preview shows while editing: the saved invoice with the form laid over it.
export function formToInvoice(form, { invoice, account }) {
  const items = form.items.map(it => ({
    date: it.date, description: it.description || 'Item', unit: it.unit,
    quantity: Number(it.quantity) || 0, unitPrice: Number(it.unitPrice) || 0,
  }));
  return {
    ...(invoice || {}),
    status:    invoice?.status || 'draft',
    billTo:    billToFrom(account),
    issueDate: form.issueDate,
    dueDate:   form.dueDate,
    notes:     form.notes,
    items,
    total: invoiceTotal(items),
  };
}

// ── Lists ─────────────────────────────────────────────────────────────────────

// Replaces the item with the same _id, or adds it to the front.
export function upsertById(list, item) {
  if (!list) return [item];
  const i = list.findIndex(x => x._id === item._id);
  if (i === -1) return [item, ...list];
  const next = list.slice();
  next[i] = item;
  return next;
}

let tempKey = 0;
export const newKey = () => `new-${++tempKey}`;
